import {
  collection,
  doc,
  getDocs,
  getDoc,
  updateDoc,
  query,
  where,
  orderBy,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../config/firebase";
import { User, MapData, UserRole, SubscriptionTier } from "../types";

// Get all users (admin only)
export const getAllUsers = async (): Promise<User[]> => {
  try {
    const usersSnapshot = await getDocs(collection(db, "users"));
    const users: User[] = [];

    usersSnapshot.forEach((doc) => {
      users.push({ id: doc.id, ...doc.data() } as User);
    });

    return users.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  } catch (error) {
    console.error("Error fetching users:", error);
    throw error;
  }
};

// Get user by ID
export const getUserById = async (userId: string): Promise<User | null> => {
  try {
    const userDoc = await getDoc(doc(db, "users", userId));
    if (userDoc.exists()) {
      return { id: userDoc.id, ...userDoc.data() } as User;
    }
    return null;
  } catch (error) {
    console.error("Error fetching user:", error);
    throw error;
  }
};

// Update user role
export const updateUserRole = async (
  userId: string,
  newRole: UserRole
): Promise<void> => {
  try {
    await updateDoc(doc(db, "users", userId), {
      role: newRole,
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error("Error updating user role:", error);
    throw error;
  }
};

// Update user subscription
export const updateUserSubscription = async (
  userId: string,
  subscriptionTier: SubscriptionTier
): Promise<void> => {
  try {
    await updateDoc(doc(db, "users", userId), {
      subscriptionTier,
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error("Error updating user subscription:", error);
    throw error;
  }
};

// Ban user account (remove admin privileges)
export const banUser = async (userId: string): Promise<void> => {
  try {
    await updateDoc(doc(db, "users", userId), {
      role: "public" as UserRole,
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error("Error banning user:", error);
    throw error;
  }
};

// Delete user completely (admin only - removes from both Auth and Firestore)
export const deleteUser = async (userId: string): Promise<void> => {
  try {
    // For now, this will just delete from Firestore
    // In production, you'd want to call a Cloud Function or API endpoint
    // that has admin privileges to delete from both Auth and Firestore

    // Delete username reservation first
    const userDoc = await getDoc(doc(db, "users", userId));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      if (userData.username) {
        await deleteDoc(doc(db, "usernames", userData.username.toLowerCase()));
      }
    }

    // Delete user document
    await deleteDoc(doc(db, "users", userId));

    // Note: To properly delete from Firebase Auth, you need to:
    // 1. Set up a Cloud Function with admin privileges, or
    // 2. Use the deleteUser.js script I created
  } catch (error) {
    console.error("Error deleting user:", error);
    throw error;
  }
};

// Get pending maps for approval
export const getPendingMaps = async (): Promise<MapData[]> => {
  try {
    const mapsRef = collection(db, "maps");
    const q = query(
      mapsRef,
      where("approvalRequested", "==", true),
      where("isRejected", "==", false),
      where("isApproved", "==", false),
      orderBy("createdAt", "desc")
    );

    const mapsSnapshot = await getDocs(q);
    const maps: MapData[] = [];

    mapsSnapshot.forEach((doc) => {
      maps.push({ id: doc.id, ...doc.data() } as MapData);
    });

    return maps;
  } catch (error: any) {
    console.error("Error fetching pending maps:", error);

    // If it's an index error, try without ordering
    if (
      error.code === "failed-precondition" ||
      error.message.includes("index")
    ) {
      try {
        const q = query(
          collection(db, "maps"),
          where("approvalRequested", "==", true),
          where("isRejected", "==", false)
        );

        const mapsSnapshot = await getDocs(q);
        const maps: MapData[] = [];

        mapsSnapshot.forEach((doc) => {
          maps.push({ id: doc.id, ...doc.data() } as MapData);
        });

        // Sort in memory
        maps.sort(
          (a, b) =>
            new Date(b.approvalRequestedAt || b.createdAt).getTime() -
            new Date(a.approvalRequestedAt || a.createdAt).getTime()
        );

        return maps;
      } catch (fallbackError) {
        console.error("Fallback query also failed:", fallbackError);
        throw fallbackError;
      }
    }

    throw error;
  }
};

// Get all maps for admin dashboard (with filters)
export const getAllMapsForAdmin = async (): Promise<MapData[]> => {
  try {
    const q = query(collection(db, "maps"), orderBy("createdAt", "desc"));

    const mapsSnapshot = await getDocs(q);
    const maps: MapData[] = [];

    mapsSnapshot.forEach((doc) => {
      maps.push({ id: doc.id, ...doc.data() } as MapData);
    });

    return maps;
  } catch (error: any) {
    console.error("Error fetching all maps:", error);

    // If it's an index error, try without ordering
    if (
      error.code === "failed-precondition" ||
      error.message.includes("index")
    ) {
      try {
        const q = query(collection(db, "maps"));

        const mapsSnapshot = await getDocs(q);
        const maps: MapData[] = [];

        mapsSnapshot.forEach((doc) => {
          maps.push({ id: doc.id, ...doc.data() } as MapData);
        });

        // Sort in memory
        maps.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        return maps;
      } catch (fallbackError) {
        console.error("Fallback query also failed:", fallbackError);
        throw fallbackError;
      }
    }

    throw error;
  }
};

// Approve map for public viewing
export const approveMap = async (mapId: string): Promise<void> => {
  try {
    await updateDoc(doc(db, "maps", mapId), {
      isApproved: true, // Mark as approved but keep private
      approvedAt: new Date().toISOString(),
      approvalRequested: false, // Clear approval request
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error("Error approving map:", error);
    throw error;
  }
};

// Reject map
export const rejectMap = async (
  mapId: string,
  reason?: string
): Promise<void> => {
  try {
    await updateDoc(doc(db, "maps", mapId), {
      isRejected: true,
      rejectionReason: reason || "Map rejected by admin",
      rejectedAt: new Date().toISOString(),
      approvalRequested: false, // Clear approval request
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error("Error rejecting map:", error);
    throw error;
  }
};

// Get system statistics
export const getSystemStats = async () => {
  try {
    // Get total users
    const usersSnapshot = await getDocs(collection(db, "users"));
    const totalUsers = usersSnapshot.size;

    // Get total maps
    const mapsSnapshot = await getDocs(collection(db, "maps"));
    const totalMaps = mapsSnapshot.size;

    // Get pending approvals
    let pendingApprovals = 0;
    try {
      const pendingMapsSnapshot = await getDocs(
        query(collection(db, "maps"), where("isPublic", "==", false))
      );
      pendingApprovals = pendingMapsSnapshot.size;
    } catch (error) {
      // Continue with 0 pending approvals
    }

    // Get active users (users who created maps in last 30 days)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const recentMapsSnapshot = await getDocs(
      query(
        collection(db, "maps"),
        where("createdAt", ">=", thirtyDaysAgo.toISOString())
      )
    );

    const activeUserIds = new Set();
    recentMapsSnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.userId) {
        activeUserIds.add(data.userId);
      }
    });

    const activeUsers = activeUserIds.size;

    return {
      totalUsers,
      totalMaps,
      pendingApprovals,
      activeUsers,
    };
  } catch (error) {
    console.error("Error fetching system stats:", error);
    throw error;
  }
};

// Get user statistics
export const getUserStats = async (userId: string) => {
  try {
    const user = await getUserById(userId);
    if (!user) return null;

    // Get user's maps
    const userMapsSnapshot = await getDocs(
      query(collection(db, "maps"), where("userId", "==", userId))
    );

    const totalMaps = userMapsSnapshot.size;
    const publicMaps = userMapsSnapshot.docs.filter(
      (doc) => doc.data().isPublic
    ).length;

    // Get total likes on user's maps
    let totalLikes = 0;
    userMapsSnapshot.forEach((doc) => {
      const data = doc.data();
      totalLikes += data.likesCount || 0;
    });

    return {
      totalMaps,
      publicMaps,
      totalLikes,
      role: user.role,
      subscriptionTier: user.subscriptionTier,
      isApproved: user.isApproved,
      createdAt: user.createdAt,
    };
  } catch (error) {
    console.error("Error fetching user stats:", error);
    throw error;
  }
};

export const getAllMapsForApprover = async (): Promise<MapData[]> => {
  try {
    const mapsRef = collection(db, "maps");
    const q = query(mapsRef, orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q);

    const maps: MapData[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      maps.push({
        id: doc.id,
        name: data.name || "Untitled Map",
        description: data.description,
        states: data.states || [],
        guideLayers: data.guideLayers || [],
        createdAt: data.createdAt?.toDate?.() || new Date(),
        updatedAt: data.updatedAt?.toDate?.() || new Date(),
        country: data.country || "Unknown",
        userId: data.userId || "",
        userDisplayName: data.userDisplayName || "Unknown User",
        userAvatar: data.userAvatar,
        isPublic: data.isPublic || false,
        likesCount: data.likesCount || 0,
        commentsCount: data.commentsCount || 0,
        viewsCount: data.viewsCount || 0,
        tags: data.tags || [],
        thumbnail: data.thumbnail,
        approvalRequested: data.approvalRequested || false,
        approvalRequestedAt: data.approvalRequestedAt,
        isApproved: data.isApproved || false,
        approvedAt: data.approvedAt,
        isRejected: data.isRejected || false,
        rejectionReason: data.rejectionReason,
        rejectedAt: data.rejectedAt,
      });
    });

    return maps;
  } catch (error) {
    console.error("Error fetching maps for approver:", error);
    throw error;
  }
};
